/**
 * @file AdoptionReport.tsx
 * @brief Company report — feature adoption / idle seats.
 *
 * @author DES
 * @date 27-07-2026
 */

import { useMemo, useState } from "react";
import { Languages, UserCheck, UserX } from "lucide-react";
import { BoardroomTable } from "@/components/icons/BoardroomTable";
import { Badge } from "@/components/ui/Badge";
import { GridBand } from "@/features/analytics/components/GridBlock";
import { StatCard } from "@/features/analytics/components/StatCard";
import type { AnalyticsRange as RangeBarValue } from "@/features/analytics/components/AnalyticsRangeBar";
import {
  ReportKpiCell,
  ReportKpiRow,
  ReportSectionHeader,
  ReportTableCell,
} from "@/features/reports-shared/ReportSection";
import { ReportTable } from "./ReportTable";
import { useAdoptionReport } from "../api";
import { defaultReportRange } from "../defaultRange";
import { ReportShell } from "./ReportShell";

function displayName(row: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}): string {
  const name = [row.first_name, row.last_name].filter(Boolean).join(" ").trim();
  return name || row.email || "—";
}

export function AdoptionReportView() {
  const [range, setRange] = useState<RangeBarValue>(() => defaultReportRange());
  const queryRange = useMemo(
    () => ({ startDate: range.startDate, endDate: range.endDate }),
    [range.startDate, range.endDate],
  );
  const report = useAdoptionReport(queryRange);
  const summary = report.data?.summary;
  const features = report.data?.feature_access;
  const rows = report.data?.employees ?? [];

  return (
    <ReportShell
      title="Feature adoption"
      description="Who used enabled products in the range. Idle = regular employees with zero usage on enabled features."
      range={range}
      onRangeChange={setRange}
      exportDisabled={rows.length === 0}
      serverExport={{
        scope: "company",
        reportKey: "adoption",
        params: {
          startDate: range.startDate,
          endDate: range.endDate,
        },
      }}
    >
      <>
        <GridBand>
          <div className="flex flex-wrap gap-2 text-sm">
            <span className="text-muted-foreground">Company features:</span>
            {features ? (
              <>
                <Badge variant={features.interpretation ? "success" : "muted"}>
                  Interpretation {features.interpretation ? "on" : "off"}
                </Badge>
                <Badge variant={features.boardroom ? "success" : "muted"}>
                  Meeting Intel {features.boardroom ? "on" : "off"}
                </Badge>
                <Badge variant={features.notes ? "success" : "muted"}>
                  Notes {features.notes ? "on" : "off"}
                </Badge>
              </>
            ) : null}
          </div>
        </GridBand>

        <ReportKpiRow className="xl:grid-cols-4">
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Active (regular)"
              icon={UserCheck}
              value={String(summary?.active_regular ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Idle (regular)"
              icon={UserX}
              value={String(summary?.idle_regular ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Used Interpretation"
              icon={Languages}
              value={String(summary?.used_interpretation ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
          <ReportKpiCell>
            <StatCard
              variant="cell"
              label="Used Meeting Intel"
              icon={BoardroomTable} // DES 08-10-2026: app icon, was Presentation
              value={String(summary?.used_boardroom ?? 0)}
              isLoading={report.isLoading}
            />
          </ReportKpiCell>
        </ReportKpiRow>

        <ReportSectionHeader
          title="Employees"
          description="Usage counts are for the selected date range."
        />
        <ReportTableCell>
          <ReportTable
            headers={[
              "Employee",
              { label: "Interpretation", align: "right" },
              { label: "Meeting Intel", align: "right" },
              { label: "Notes", align: "right" },
              { label: "Adoption", align: "right" },
            ]}
            isLoading={report.isLoading}
            empty={rows.length === 0}
          >
            {rows.map((row) => (
              <tr key={row.user_id} className="hover:bg-secondary/20">
                <td className="px-4 py-2">
                  <div className="font-medium">{displayName(row)}</div>
                  <div className="text-xs text-muted-foreground">
                    {row.email} · {row.role}
                  </div>
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.interpretation_sessions}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.meeting_count}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {row.note_count}
                </td>
                <td className="px-4 py-2 text-right">
                  {row.idle ? (
                    <Badge variant="warning">Idle</Badge>
                  ) : row.active_products.length > 0 ? (
                    <Badge variant="success">
                      {row.active_products.length} active
                    </Badge>
                  ) : (
                    <Badge variant="muted">—</Badge>
                  )}
                </td>
              </tr>
            ))}
          </ReportTable>
        </ReportTableCell>
      </>
    </ReportShell>
  );
}
